import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import {
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import Pagination from "../../../components/CustomPagination";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import { formatUserDetails } from "../../../utils/helperMethods";
import UserDetailsDrawer from "../../../components/Drawers/UserDetailsDrawer";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { UserDashboardStats } from "../../../components/Provider/UserDashboardStats";
import {
  useAssessmentMetrics,
  useGetAllSponsorsForAdmin,
  useGetAllSponsorUsersList,
  useGetSponsorUserDetails,
  useSponsorRFIMetrics,
  useGetRequestMetrics,
  useActivateOrDeactivateSponsorUserContact,
  useActivateOrDeactivateSponsor,
} from "../../../custom-hooks/queries";
import UsersListTable, {
  ActiveStatusCell,
  formatCreatedDate,
} from "../../../components/UsersListTable";
import { getLocationFromProvider } from "../../../utils/addressUtils";
import { getPortalAccessLabel, getPortalAccessLabelFromSponsor } from "../../../utils/roles";
import ActivateOrDeactivateConfirmationModal from "../../../components/ActivateOrDeactivateConfirmationModal";

const initialActivateDeactivateModal = {
  open: false,
  kind: null,
  row: null,
  targetActive: null,
};

/** Organization name + optional logo for user rows */
function OrganizationCell({ item }) {
  const fromItem =
    item?.sponsor_name ||
    item?.organization_name ||
    item?.sponsor_organization_name;
  const name = fromItem || "";
  const logoUrl =
    item?.logo ||
    item?.logo_url ||
    item?.sponsor_logo_url ||
    item?.organization_logo_url ||
    item?.vendor_logo ||
    item?.sponsor_logo;
  const initial = name ? name.trim().charAt(0).toUpperCase() : "—";

  return (
    <div className="flex items-center gap-2">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="h-6 w-6 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <span
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-medium text-indigo-700"
          aria-hidden
        >
          {initial}
        </span>
      )}
      <span className="truncate">{name || "—"}</span>
    </div>
  );
}

const SponsorUserGrid = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeTab, setActiveTab] = useState("organizations");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({});
  const [isSearchOpenByUser, setIsSearchOpenByUser] = useState(false);
  const searchInputRef = useRef(null);
  const [activateDeactivateModal, setActivateDeactivateModal] = useState(initialActivateDeactivateModal);

  const sponsorSearchQuery =
    useSelector(
      (state) => state.providersFilters?.searchTextByPage?.["sponsorOrganizations"]
    ) ?? "";
  const userSearchQuery =
    useSelector(
      (state) => state.providersFilters?.searchTextByPage?.["sponsorUsers"]
    ) ?? "";

  const currentSearchQuery =
    activeTab === "organizations" ? sponsorSearchQuery : userSearchQuery;
  const [searchInputValue, setSearchInputValue] = useState(currentSearchQuery);
  const isSearchExpanded = searchInputValue.length > 0 || isSearchOpenByUser;
  useEffect(() => {
    setSearchInputValue(currentSearchQuery);
    setIsSearchOpenByUser(currentSearchQuery.length > 0);
  }, [currentSearchQuery, activeTab]);

  useEffect(() => {
    return () => {
      dispatch(setSearchTextByPage({ pageKey: "sponsorOrganizations", searchText: "" }));
      dispatch(setSearchTextByPage({ pageKey: "sponsorUsers", searchText: "" }));
      queryClient.removeQueries({ queryKey: ["sponsors"] });
      queryClient.removeQueries({ queryKey: ["getAllSponsorUsersList"] });
    };
  }, [dispatch, queryClient]);

  const organizationPayload = useMemo(
    () => ({
      page_size: 21,
      current_page: currentPage,
      search_text: sponsorSearchQuery,
      country: "",
      city: "",
      state: "",
      is_active: null,
    }),
    [currentPage, sponsorSearchQuery]
  );

  const {
    data: organizationsData,
    isLoading: isLoadingOrganizations,
    error: organizationsError,
  } = useGetAllSponsorsForAdmin(organizationPayload);

  const { mutate: mutateActivateOrDeactivateSponsor } = useActivateOrDeactivateSponsor();
  const { mutate: mutateActivateOrDeactivateSponsorUserContact } = useActivateOrDeactivateSponsorUserContact();

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetAllSponsorUsersList({ currentPage, userSearchQuery });

  const { data: sponsorMetrics } = useGetRequestMetrics();
  const { data: userDetails } = useGetSponsorUserDetails(expandedRow);

  const SPONSOR_STATS = useMemo(
    () => [
      {
        name: "Total Organizations",
        value: organizationsData?.total_items ?? organizationsData?.total_count ?? 0,
      },
      {
        name: "Total Users",
        value: usersData?.total_items ?? usersData?.total_count ?? 0,
      },
      {
        name: "Total Requests",
        value:
          sponsorMetrics?.total_requests ??
          0,
      },
      {
        name: "Active Requests",
        value:
          sponsorMetrics?.active_requests ??
          0,
      },
    ],
    [
      organizationsData?.total_items,
      organizationsData?.total_count,
      usersData?.total_items,
      usersData?.total_count,
      sponsorMetrics?.total_requests,
      sponsorMetrics?.active_requests,
    ]
  );

  const debouncedSponsorSearch = useMemo(
    () =>
      debounce((search) => {
        dispatch(
          setSearchTextByPage({
            pageKey: "sponsorOrganizations",
            searchText: search,
          })
        );
      }, 300),
    [dispatch]
  );

  const debouncedUserSearch = useMemo(
    () =>
      debounce((search) => {
        dispatch(
          setSearchTextByPage({
            pageKey: "sponsorUsers",
            searchText: search,
          })
        );
      }, 300),
    [dispatch]
  );

  useEffect(() => setCurrentPage(1), [sponsorSearchQuery, userSearchQuery]);

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  useEffect(() => {
    if (tab === "users") setActiveTab("users");
  }, [tab]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (Object.keys(prev).length === 1 && prev[key]) {
        return {
          [key]: { direction: prev[key].direction === "asc" ? "desc" : "asc" },
        };
      }
      return { [key]: { direction: "asc" } };
    });
  };

  const orgItems = organizationsData?.items ?? [];
  const userItems = usersData?.items ?? [];
  const activeData = activeTab === "organizations" ? orgItems : userItems;

  const sortedItems = useMemo(() => {
    if (!activeData?.length) return [];
    const sortKey = Object.keys(sortConfig)[0];
    if (!sortKey) return activeData;
    const { direction } = sortConfig[sortKey];
    return [...activeData].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (sortKey === "primary_location") {
        aVal = getLocationFromProvider(
          { city: a.city, state: a.state, country: a.country },
          { short: false, abbvState: true }
        );
        bVal = getLocationFromProvider(
          { city: b.city, state: b.state, country: b.country },
          { short: false, abbvState: true }
        );
      }
      if (sortKey === "organization") {
        aVal =
          a.sponsor_name ||
          a.organization_name ||
        a.sponsor_organization_name ||
          "";
        bVal =
          b.sponsor_name ||
          b.organization_name ||
        b.sponsor_organization_name ||
          "";
      }
      if (sortKey === "portal_access") {
        aVal = getPortalAccessLabelFromSponsor(a);
        bVal = getPortalAccessLabelFromSponsor(b);
      }
      if (sortKey === "name" && activeTab === "users") {
        aVal = [a.first_name, a.last_name].filter(Boolean).join(" ");
        bVal = [b.first_name, b.last_name].filter(Boolean).join(" ");
      }
      if (sortKey === "sponsor_name" && activeTab === "organizations") {
        aVal = a.sponsor_name || "";
        bVal = b.sponsor_name || "";
      }
      if (sortKey === "created" || sortKey === "created_at") {
        aVal = a.created_at ?? a.created;
        bVal = b.created_at ?? b.created;
      }
      if (aVal == null) return direction === "asc" ? 1 : -1;
      if (bVal == null) return direction === "asc" ? -1 : 1;
      if (
        sortKey === "is_active" &&
        typeof aVal === "boolean" &&
        typeof bVal === "boolean"
      ) {
        return direction === "asc"
          ? aVal === bVal
            ? 0
            : aVal
            ? -1
            : 1
          : aVal === bVal
          ? 0
            : aVal
          ? 1
          : -1;
      }
      if (typeof aVal === "string" && typeof bVal === "string") {
        return direction === "asc"
          ? aVal.localeCompare(bVal, undefined, { sensitivity: "base" })
          : bVal.localeCompare(aVal, undefined, { sensitivity: "base" });
      }
      if (aVal instanceof Date || bVal instanceof Date) {
        const aT = new Date(aVal).getTime();
        const bT = new Date(bVal).getTime();
        return direction === "asc" ? aT - bT : bT - aT;
      }
      return direction === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }, [activeData, sortConfig, activeTab]);


  const handleManageSponsor = useCallback(
    (row) => navigate(`/Backend/Users/AddSponsors/${row?.sponsor_id}`),
    [navigate]
  );

  const openSponsorOrgActivateDeactivateModal = useCallback((row, targetActive) => {
    if (!row?.sponsor_id) return;
    setActivateDeactivateModal({ open: true, kind: "sponsorOrganization", row, targetActive });
  }, []);

  const handleDeactivateSponsor = useCallback((row) => {
    openSponsorOrgActivateDeactivateModal(row, false);
  }, [openSponsorOrgActivateDeactivateModal]);

  const handleActivateSponsor = useCallback((row) => {
    openSponsorOrgActivateDeactivateModal(row, true);
  }, [openSponsorOrgActivateDeactivateModal]);

  const handleExpandRow = useCallback((rowId) => {
    setExpandedRow((prev) => {
      if (prev === rowId) {
        dispatch(setDrawer(false));
        return null;
      }
      dispatch(setDrawer(true));
      return rowId;
    });
  }, [dispatch]);

  const handleManageUser = useCallback(
    (row) =>
      navigate(
        `/Backend/Users/Sponsors/AddUser?userId=${row?.id ?? row?.user_id}`
      ),
    [navigate]
  );

  const openSponsorUserActivateDeactivateModal = useCallback((row, targetActive) => {
    if (!row?.id && !row?.user_id) return;
    setActivateDeactivateModal({ open: true, kind: "sponsorUser", row, targetActive });
  }, []);

  const handleDeactivateUser = useCallback((row) => {
    openSponsorUserActivateDeactivateModal(row, false);
  }, [openSponsorUserActivateDeactivateModal]);

  const handleActivateUser = useCallback((row) => {
    openSponsorUserActivateDeactivateModal(row, true);
  }, [openSponsorUserActivateDeactivateModal]);

  const handleConfirmActivateDeactivate = useCallback(() => {
    const { kind, row, targetActive } = activateDeactivateModal;
    if (!row || targetActive == null) return;
    if (kind === "sponsorOrganization") {
      mutateActivateOrDeactivateSponsor({ sponsor_id: row.sponsor_id, is_active: targetActive });
    } else if (kind === "sponsorUser") {
      mutateActivateOrDeactivateSponsorUserContact({
        sponsor_user_id: row.id ?? row.user_id,
        is_active: targetActive,
      });
    }
    setActivateDeactivateModal(initialActivateDeactivateModal);
  }, [activateDeactivateModal, mutateActivateOrDeactivateSponsor, mutateActivateOrDeactivateSponsorUserContact]);

  const closeActivateDeactivateModal = useCallback(() => {
    setActivateDeactivateModal(initialActivateDeactivateModal);
  }, []);

  const activateDeactivateEntityName = useMemo(() => {
    const row = activateDeactivateModal.row;
    if (!row) return "";
    if (activateDeactivateModal.kind === "sponsorOrganization") {
      return row?.sponsor_name || "—";
    }
    return [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim() || row?.email || "—";
  }, [activateDeactivateModal.row, activateDeactivateModal.kind]);

  const handleAddNewAccount = useCallback(() => {
    navigate("/Backend/Users/AddSponsors");
  }, [navigate]);

  const handleAddNewUser = useCallback(() => {
    navigate("/Backend/Users/Sponsors/AddUser");
  }, [navigate]);

  const organizationColumns = useMemo(
    () => [
      {
        key: "sponsor_name",
        header: "Name",
        sortable: true,
        width: "18%",
        cellClassName: "text-gray-900",
        render: (item) => {
          const logoUrl =
            item?.logo ||
            item?.logo_url ||
            item?.sponsor_logo_url ||
            item?.organization_logo_url ||
            item?.vendor_logo ||
            item?.sponsor_logo;
          return (
          <div className="flex items-center gap-2">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-medium text-indigo-700">
                    {(item?.sponsor_name || "?")[0].toUpperCase()}
                  </div>
              )}
            <span
              className="text-sm font-medium text-gray-900 break-words"
            >
              {item?.sponsor_name || "—"}
            </span>
          </div>
          )
        },
      },
      {
        key: "primary_location",
        header: "Primary Location",
        sortable: true,
        width: "16%",
        cellClassName: "text-gray-600",
        render: (item) =>
          getLocationFromProvider(
            { city: item?.city, state: item?.state, country: item?.country },
            { short: false, abbvState: true }
          ) || "—",
      },
      {
        key: "is_active",
        header: "Status",
        sortable: true,
        width: "12%",
        render: (item) => (
          <ActiveStatusCell isActive={item?.is_active} lastActivityAt={item?.last_login_datetime ?? item?.lastlogin ?? ""} />
        ),
      },
      {
        key: "created",
        header: "Created",
        sortable: true,
        width: "12%",
        cellClassName: "text-gray-700",
        render: (item) =>
          formatCreatedDate(item?.created_at ?? item?.created_date ?? item?.create_date_time),
      },
      {
        key: "requests_count",
        header: "Requests",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) => item?.requests_count ?? "—",
      },
      // {
      //   key: "credits",
      //   header: "Credits",
      //   sortable: true,
      //   width: "10%",
      //   cellClassName: "text-gray-700",
      //   render: (item) => item?.credits ?? item?.credits_count ?? 0,
      // },
    ],
    []
  );

  const organizationActionMenuItems = useMemo(
    () => [
      {
        label: "Manage Organization",
        icon: <PencilSquareIcon className="h-5 w-5" />,
        onClick: handleManageSponsor,
      },
      {
        label: "Deactivate Account",
        icon: <NoSymbolIcon className="h-5 w-5" />,
        onClick: handleDeactivateSponsor,
        showItem: (item) => item?.is_active,
      },
      {
        label: "Activate Account",
        icon: <CheckCircleIcon className="h-5 w-5" />,
        onClick: handleActivateSponsor,
        showItem: (item) => !item?.is_active,
      },
    ],
    [handleManageSponsor, handleDeactivateSponsor, handleActivateSponsor]
  );

  const userColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-900",
        render: (item) => (
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-medium text-gray-900 break-words transition-colors cursor-pointer hover:text-indigo-600"
              onClick={() => handleExpandRow(item?.id ?? item?.user_id)}
            >
              {[item?.first_name, item?.last_name].filter(Boolean).join(" ") ||
                "—"}
            </span>
          </div>
        ),
      },
      {
        key: "organization",
        header: "Organization",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-700",
        render: (item) => (
          <OrganizationCell
            item={item}
          />
        ),
      },
      {
        key: "title",
        header: "Title",
        sortable: true,
        width: "12%",
        cellClassName: "text-gray-700",
        render: (item) => item?.job_title || item?.title || "—",
      },
      {
        key: "email",
        header: "Email",
        sortable: true,
        width: "14%",
        cellClassName: "text-gray-700",
        render: (item) => (
          <a
            href={`mailto:${item?.email}`}
            className="text-gray-900 hover:text-indigo-600"
          >
            {item?.email || "—"}
          </a>
        ),
      },
      {
        key: "is_active",
        header: "Active Status",
        sortable: true,
        width: "10%",
        render: (item) => (
          <ActiveStatusCell
            isActive={item?.is_active}
            lastActivityAt={item?.last_login_datetime ?? item?.lastlogin}
          />
        ),
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) =>
          item?.sponsor_subrole_dto
            ?.map((r) => r.sub_role)
            .join("/") ?? "—",
      },
      {
        key: "portal_access",
        header: "Portal Access",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) => getPortalAccessLabel(item),
      },
      {
        key: "created_at",
        header: "Created",
        sortable: true,
        width: "10%",
        cellClassName: "text-gray-700",
        render: (item) =>
          formatCreatedDate(item?.created_at ?? item?.created_date ?? item?.create_date_time),
      },
    ],
    [handleExpandRow]
  );

  const userActionMenuItems = useMemo(
    () => [
      {
        label: "Manage",
        icon: <PencilSquareIcon className="h-4 w-4" />,
        onClick: handleManageUser,
      },
      {
        label: "Deactivate Account",
        icon: <NoSymbolIcon className="h-4 w-4" />,
        onClick: handleDeactivateUser,
        showItem: (item) => item?.is_active === true,
      },
      {
        label: "Activate Account",
        icon: <CheckCircleIcon className="h-4 w-4" />,
        onClick: handleActivateUser,
        showItem: (item) => item?.is_active === false,
      },
    ],
    [handleManageUser, handleDeactivateUser, handleActivateUser]
  );

  const isLoading =
    activeTab === "organizations" ? isLoadingOrganizations : isLoadingUsers;
  const error = activeTab === "organizations" ? organizationsError : usersError;
  const totalPages =
    activeTab === "organizations"
      ? organizationsData?.total_pages ?? 1
      : usersData?.total_pages ?? 1;
  const isSearchActive =
    activeTab === "organizations"
      ? !!sponsorSearchQuery
      : !!userSearchQuery;

  // if (error) {
  //   return (
  //     <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
  //       <p className="text-red-600">Error fetching data. Please try again later.</p>
  //     </div>
  //   );
  // }

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="">
        <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-white py-6 flex flex-col items-start justify-between -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <h1 className="text-lg font-medium text-gray-900">Sponsors</h1>
            <p className="mt-1 text-sm text-gray-500">
              Overview of Sponsor statistics and activities.
            </p>
          </div>

          <div className="">
            <UserDashboardStats stats={SPONSOR_STATS} />
          </div>
        </div>

        <div className="flex sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200 mb-4 mt-10 px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8 pt-4">
            <button
              onClick={() => setActiveTab("organizations")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "organizations"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                }`}
            >
              Organizations
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "users"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                }`}
            >
              Users
            </button>
          </nav>
          <div className="flex items-center gap-3 pb-3">
            <div
              className={`relative flex items-center transition-all duration-300 ease-in-out ${isSearchExpanded ? "w-64" : "w-auto"}`}
            >
              {isSearchExpanded ? (
                <div className="relative w-full">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={
                      activeTab === "organizations"
                        ? "Search organizations..."
                        : "Search users..."
                    }
                    value={searchInputValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSearchInputValue(v);
                      if (activeTab === "organizations")
                        debouncedSponsorSearch(v);
                      else debouncedUserSearch(v);
                    }}
                    onBlur={() =>
                      !searchInputValue && setIsSearchOpenByUser(false)
                    }
                    className="block w-full rounded-md border-0 py-1.5 pl-9 pr-3 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsSearchOpenByUser(true)}
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                  aria-label="Search"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            {/* <button
              type="button"
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-50"
              aria-label="More options"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button> */}
          </div>
        </div>

        <div className="bg-white shadow border border-gray-200 rounded-lg">
          <div className="flex justify-end px-4 sm:px-6 lg:px-8 pt-6 pb-4">
            <button
              type="button"
              onClick={
                activeTab === "organizations"
                  ? handleAddNewAccount
                  : handleAddNewUser
              }
              className="rounded-md bg-indigo-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {activeTab === "organizations" ? "Add Sponsor" : "Add User"}
            </button>
          </div>

          {isLoading ? (
            <SkeletonLoader />
          ) : (
              <UsersListTable
                className="shadow-sm mt-6"
                getRowClassName={(item) => !item.is_active ? "opacity-50" : ""}
                columns={
                  activeTab === "organizations"
                    ? organizationColumns
                    : userColumns
                }
                data={sortedItems}
                actionMenuItems={
                  activeTab === "organizations"
                    ? organizationActionMenuItems
                    : userActionMenuItems
                }
                sortConfig={sortConfig}
                onSort={handleSort}
                rowKey={
                  activeTab === "organizations" ? "sponsor_id" : "id"
                }
                emptyMessage={`No ${activeTab === "organizations" ? "organizations" : "users"} found`}
                emptyDescription={
                  activeTab === "organizations"
                    ? "No sponsor organizations to display."
                    : "No sponsor users to display."
                }
                isSearchActive={isSearchActive}
            />
          )}

          {sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      </div>

      {expandedRow && userDetails && (
        <UserDetailsDrawer
          details={formatUserDetails(userDetails)}
          isLoading={!!expandedRow && !userDetails}
          onClose={() => {
            setExpandedRow(null);
            dispatch(setDrawer(false));
          }}
        />
      )}

      <ActivateOrDeactivateConfirmationModal
        open={activateDeactivateModal.open}
        onClose={closeActivateDeactivateModal}
        onConfirm={handleConfirmActivateDeactivate}
        type={activateDeactivateModal.kind === "sponsorOrganization" ? "sponsor" : "user"}
        isDeactivated={activateDeactivateModal.targetActive === true}
        entityName={activateDeactivateEntityName}
      />
    </div>
  );
};

export default SponsorUserGrid;
